import ApiAction from "../../enums/ApiAction";
import { boolOrIntCodec, nonEmptyArray, timestampCodec } from "../../validation/codecs";
import { mixed, optional, required, t } from "../../validation/validator";
import { CallResponseMixinSchema, CallResponseSchema } from "../common/callResponseSchema";
import { ScenarioResponseSchema } from "../scenarios/scenarioSchemas";
import { TagRecordSchema } from "../tags/tagSchemas";

/****************************************************
 * Playlist
 */
const commonPlaylistFields = {
    name: t.string,
    description: t.string,
    image_url: t.union([t.string, t.undefined, t.null]),
    video_url: t.union([t.string, t.undefined, t.null]),
    published: t.union([boolOrIntCodec, t.undefined]),
    start_at: t.union([timestampCodec, t.undefined, t.null]),
    end_at: t.union([timestampCodec, t.undefined, t.null]),
    archived: t.union([boolOrIntCodec, t.undefined]),
    show_leaderboard: t.union([boolOrIntCodec, t.undefined]),
    show_results: t.union([boolOrIntCodec, t.undefined]),
    game_speed: t.union([t.number, t.undefined, t.null]),
    bullet_time_decisions: t.union([boolOrIntCodec, t.undefined, t.null]),
    memory_player_count: t.union([t.number, t.undefined, t.null]),
    memory_time_limit: t.union([t.number, t.undefined, t.null]),
    memory_distance: t.union([t.number, t.undefined, t.null]),
    mirrored: t.union([boolOrIntCodec, t.undefined, t.null])
};

// Response object for account info
const AccountResponseSchema = t.type({
    id: t.number,
    name: t.string,
    type: t.string,
    created_at: timestampCodec
});

// Response object for shared account info
const SharedAccountResponseSchema = t.intersection([
    AccountResponseSchema,
    t.type({
        shared_by: t.type({
            id: t.number,
            name: t.string
        }),
        shared_at: timestampCodec
    })
]);

// Response object for team info
const TeamResponseSchema = t.type({
    id: t.number,
    name: t.string,
    created_at: timestampCodec
});

// Response object for shared team info
const SharedTeamResponseSchema = t.intersection([
    TeamResponseSchema,
    t.type({
        shared_by: t.type({
            id: t.number,
            name: t.string
        }),
        shared_at: timestampCodec
    })
]);

// Response object for user info
const UserResponseSchema = t.type({
    id: t.number,
    name: t.string,
    created_at: timestampCodec
});

// Response object for shared user info
const SharedUserResponseSchema = t.intersection([
    UserResponseSchema,
    t.type({
        shared_by: t.type({
            id: t.number,
            name: t.string
        }),
        shared_at: timestampCodec
    })
]);

// Response object for playlist scenario config
export const PlaylistScenarioConfigSchema = t.intersection([
    t.type({
        scenario_id: t.number,
    }),
    t.partial({
        game_speed: t.union([t.number, t.null]),
        bullet_time_decisions: t.union([boolOrIntCodec, t.null]),
        memory_player_count: t.union([t.number, t.null]),
        memory_time_limit: t.union([t.number, t.null]),
        memory_distance: t.union([t.number, t.null]),
        mirrored: t.union([boolOrIntCodec, t.null]),
        index: t.union([t.number, t.null]),
    })
]);

export type PlaylistScenarioConfig = t.TypeOf<typeof PlaylistScenarioConfigSchema>;

// Internal object for playlist scenario as it comes from the database
export const PlaylistResponseScenarioSchema = t.intersection([
    t.type({
        id: t.number,
    }),
    t.partial({
        doc_id: t.union([t.string, t.null]),
        game_speed: t.union([t.number, t.null]),
        bullet_time_decisions: t.union([boolOrIntCodec, t.null]),
        memory_player_count: t.union([t.number, t.null]),
        memory_time_limit: t.union([t.number, t.null]),
        memory_distance: t.union([t.number, t.null]),
        mirrored: t.union([boolOrIntCodec, t.null]),
        index: t.union([t.number, t.null]),
    })
]);

export type PlaylistResponseScenario = t.TypeOf<typeof PlaylistResponseScenarioSchema>;

// Response object for playlist scenario
export const PlaylistScenarioSchema = t.intersection([
    PlaylistScenarioConfigSchema,
    t.type({
        scenario: ScenarioResponseSchema
    })
]);

// Response object for playlist
export const PlaylistResponseSchema = t.intersection([
    t.type({
        id: t.number,
        created_at: timestampCodec,
        updated_at: t.union([timestampCodec, t.undefined, t.null]),
        scenario_count: t.number,
        account: t.union([AccountResponseSchema, t.null]),
        shared_accounts: t.array(SharedAccountResponseSchema),
        shared_teams: t.array(SharedTeamResponseSchema),
        shared_users: t.array(SharedUserResponseSchema),
    }),
    t.partial({
        ...commonPlaylistFields,
        tags: t.union([t.array(TagRecordSchema), t.null]),
    })
]);

/****************************************************
 * CreatePlaylist
 */
export const CreatePlaylistSchema = t.intersection([
    t.type({
        name: t.string,
        account_id: t.number,
        scenarios: nonEmptyArray(PlaylistScenarioConfigSchema),
    }),
    t.partial({
        ...commonPlaylistFields,
        shared_with_accounts: t.union([t.array(t.number), t.undefined]),
        shared_with_teams: t.union([t.array(t.number), t.undefined]),
        shared_with_users: t.union([t.array(t.number), t.undefined]),
        tags: t.union([t.array(t.string), t.undefined])
    })
]);

/****************************************************
 * UpdatePlaylist
 * 
 * Scenario update modes:
 * - 'replace' (default): Full replacement, `delete_scenario_ids` is ignored
 * - 'add': Processes `delete_scenario_ids` first, then adds scenarios
 * - Delete only: Provide only `delete_scenario_ids` without scenarios
 */
export const UpdatePlaylistSchema = t.intersection([
    t.type({
        id: t.number,
    }),
    t.partial({
        ...commonPlaylistFields,
        scenario_action: t.union([t.literal(ApiAction.ADD), t.literal(ApiAction.REPLACE)]),
        scenarios: t.array(PlaylistScenarioConfigSchema),
        delete_scenario_ids: t.array(t.number),
        shared_with_accounts: t.array(t.number),
        shared_with_teams: t.array(t.number),
        shared_with_users: t.array(t.number),
        tags: t.array(t.string)
    })
]);

/****************************************************
 * GetPlaylists
 */
export const GetPlaylistsSchema = mixed([
    required({
        account_id: t.number,
    }),
    optional({
        shared_with_user_ids: t.union([t.array(t.number), t.undefined]),
        shared_with_team_ids: t.union([t.array(t.number), t.undefined]),
        shared_with_account_ids: t.union([t.array(t.number), t.undefined]),
        ids: t.union([t.array(t.number), t.undefined]),
        unpublished_allowed: t.union([t.boolean, t.undefined]),
        include_tags: t.union([t.array(t.string), t.undefined]),
        exclude_tags: t.union([t.array(t.string), t.undefined]),
        limit: t.union([t.number, t.undefined, t.null]),
        order_by: t.union([t.array(t.type({
            field: t.string,
            direction: t.union([t.literal('asc'), t.literal('desc')])
        })), t.undefined, t.null])
    })
]);

/****************************************************
 * CreatePlaylistSession
 */
export const CreatePlaylistSessionSchema = t.type({
    playlist_id: t.number,
    account_id: t.union([t.number, t.undefined, t.null])
});

/****************************************************
 * SharePlaylist
 */
export const SharePlaylistSchema = t.type({
    playlist_id: t.number,
    user_ids: t.union([t.array(t.number), t.undefined]),
    team_ids: t.union([t.array(t.number), t.undefined]),
    account_ids: t.union([t.array(t.number), t.undefined]),
    remove_existing: t.union([t.boolean, t.undefined])
});

/****************************************************
 * DeletePlaylist
 */
export const DeletePlaylistSchema = t.type({
    playlist_id: t.number,
    archive_only: t.union([t.boolean, t.undefined])
});

/****************************************************
 * GetPlaylistResults
 */
export const GetPlaylistResultsSchema = t.type({
    user_id: t.string,
    playlist_id: t.number
});

// Response schema for playlist results
export const PlaylistResultsResponseSchema = t.type({
    playlist_id: t.number,
    created_at: t.union([timestampCodec, t.null]),
    playlist_sessions: t.array(t.type({
        status: t.string,
        play_session: t.type({
            id: t.number,
            type: t.string,
            game_mode: t.string,
            play_position: t.union([t.string, t.null]),
            experience_gained: t.number,
            is_completed: t.boolean,
            user_rating: t.union([t.number, t.null]),
            game_speed: t.union([t.number, t.null]),
            created_at: timestampCodec,
            updated_at: t.union([timestampCodec, t.null])
        }),
        results: t.type({
            labels_made: t.union([t.number, t.null]),
            labels_succeeded: t.union([t.number, t.null]),
            receivals_made: t.union([t.number, t.null]),
            receivals_succeeded: t.union([t.number, t.null]),
            tackles_made: t.union([t.number, t.null]),
            tackles_succeeded: t.union([t.number, t.null]),
            shots_made: t.union([t.number, t.null]),
            shots_succeeded: t.union([t.number, t.null]),
            passes_made: t.union([t.number, t.null]),
            passes_succeeded: t.union([t.number, t.null]),
            passes_score: t.union([t.number, t.null]),
            passes_packing_rate: t.union([t.number, t.null]),
            awareness_score: t.union([t.number, t.null]),
            total_score: t.union([t.number, t.null]),
            scan_score: t.union([t.number, t.null]),
            scan_rate: t.union([t.number, t.null]),
            scan_timing: t.union([t.number, t.null]),
            critical_scan_score: t.union([t.number, t.null]),
            critical_scans: t.union([t.number, t.null]),
            byb_iq: t.union([t.number, t.null])
        }),
        scenarios: t.array(t.type({
            scenario_id: t.number,
            scenario_doc_id: t.string
        }))
    }))
});

// Types
export type PlaylistResponse = t.TypeOf<typeof PlaylistResponseSchema>;
export type CreatePlaylistRequest = t.TypeOf<typeof CreatePlaylistSchema>;
export type UpdatePlaylistRequest = t.TypeOf<typeof UpdatePlaylistSchema>;
export type GetPlaylistsRequest = t.TypeOf<typeof GetPlaylistsSchema>;
export type CreatePlaylistSessionRequest = t.TypeOf<typeof CreatePlaylistSessionSchema>;
export type SharePlaylistRequest = t.TypeOf<typeof SharePlaylistSchema>;
export type DeletePlaylistRequest = t.TypeOf<typeof DeletePlaylistSchema>;
export type GetPlaylistResultsRequest = t.TypeOf<typeof GetPlaylistResultsSchema>;
export type PlaylistResultsResponse = t.TypeOf<typeof PlaylistResultsResponseSchema>;
export type PlaylistScenario = t.TypeOf<typeof PlaylistScenarioSchema>;

// Client API
export const PlaylistApis = {
    Playlists: {
        CreatePlaylist: {
            Name: "playlistsV2-temp-createPlaylist",
            Request: CreatePlaylistSchema,
            Response: CallResponseSchema,
        },
        UpdatePlaylist: {
            Name: "playlistsV2-updatePlaylist", 
            Request: UpdatePlaylistSchema,
            Response: CallResponseSchema,
        },
        GetPlaylists: {
            Name: "playlistsV2-temp-getPlaylists",
            Request: GetPlaylistsSchema,
            Response: mixed([required({ data: t.array(PlaylistResponseSchema) }), CallResponseMixinSchema]),
        },
        CreatePlaylistSession: {
            Name: "playlistsV2-temp-createPlaylistSession",
            Request: CreatePlaylistSessionSchema,
            Response: CallResponseSchema,
        },
        SharePlaylist: {
            Name: "playlistsV2-sharePlaylist",
            Request: SharePlaylistSchema,
            Response: CallResponseSchema,
        },
        DeletePlaylist: {
            Name: "playlistsV2-deletePlaylist",
            Request: DeletePlaylistSchema,
            Response: CallResponseSchema,
        },
        GetPlaylistResults: {
            Name: "playlistsV2-getPlaylistResults",
            Request: GetPlaylistResultsSchema,
            Response: mixed([required({ data: PlaylistResultsResponseSchema }), CallResponseMixinSchema]),
        }
    }
}; 